import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  type CountedLibrary,
  gamesHoldingAnUnlock,
  type RarityStatus,
  useLibraryRarity,
} from "../api-client/use-library-rarity";
import {
  type ShownGames,
  useShownAchievementNames,
} from "../api-client/use-shown-achievement-names";
import type { LibraryView } from "./library";
import { buildRarestUnlocks, gamesShownIn, type NamedUnlock, nameUnlocks } from "./rarest-unlocks";

export type RarestTab = {
  /**
   * Where the ranking stands. It is phase one's, because phase one is what
   * decides whether there is anything to rank: naming a row cannot add one, and
   * an empty list is only news once every figure is in.
   */
  readonly status: RarityStatus;
  /** Rarest first, named and iconed where their games have said. */
  readonly rows: readonly NamedUnlock[];
  /** `rarest 10 across 214 games counted`, and what it excludes. */
  readonly countedLabel: string;
  /**
   * How far phase one has got, between 0 and 1, or null when nothing of it is
   * outstanding.
   *
   * Phase one alone, because phase two is three to six schemas in one wave: a
   * share of it would be 0 for the whole of it, so the bar would fill, vanish,
   * come back empty and vanish again. And the rows say which of them are
   * waiting, each in the space its name will fill — a bar saying only that
   * something somewhere is outstanding adds nothing to that (#66).
   */
  readonly loaded: number | null;
  /**
   * Whether the counted library holds an unlock at all.
   *
   * An empty ranking means two different things and only one of them is about
   * the player: a library with nothing unlocked in it, and a library Steam
   * publishes no figure for. Telling the second player they have unlocked
   * nothing would be a plain untruth — they are the ones holding the trophies.
   */
  readonly anyUnlock: boolean;
};

/**
 * Everything behind the library's Rarest tab, assembled: the two phases, the
 * ranking they feed, and the one share the stats card's load bar draws.
 *
 * Nothing here decides anything a reader sees — `buildRarestUnlocks` and
 * `nameUnlocks` do, and they are pure. This holds the order the two loads have
 * to run in, which is the whole of why the tab is affordable: phase two asks
 * only about the games phase one's finished ranking shows (#31, ADR-0005).
 *
 * `library` is null for as long as there is nothing counted to rank, and must
 * keep a stable identity across renders — the loads are started off it.
 */
export const useRarestTab = (
  library: CountedLibrary | null,
  view: LibraryView,
  active: boolean,
): RarestTab => {
  const { t } = useTranslation();
  const { rarity, status, loaded } = useLibraryRarity(library, active);

  const ranking = useMemo(() => buildRarestUnlocks(view, rarity, t), [view, rarity, t]);
  const appIds = useMemo(() => gamesShownIn(ranking.rows), [ranking.rows]);

  /**
   * Null until phase one is through, and not merely until it has ranked
   * something.
   *
   * A ranking is rebuilt on every wave of figures, so a load half-way through
   * shows whatever the waves so far happened to hold — and every game it passes
   * through is asked for and remembered. Arming this on a partial ranking would
   * spend the 253 KB schema on games that then drop out of it, which is the
   * cost ADR-0005 took off this screen in the first place.
   */
  const shown = useMemo<ShownGames | null>(
    () =>
      library === null || status !== "ready" || appIds.length === 0
        ? null
        : { client: library.client, appIds },
    [library, status, appIds],
  );
  const { names, pending } = useShownAchievementNames(shown);

  const rows = useMemo(
    () => nameUnlocks(ranking.rows, names, pending),
    [ranking.rows, names, pending],
  );

  return {
    status,
    rows,
    countedLabel: ranking.countedLabel,
    loaded,
    anyUnlock: library !== null && gamesHoldingAnUnlock(library.tallies).length > 0,
  };
};
